import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials as mockTestimonials } from '../data/mockData';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch Google reviews from backend API
    const fetchGoogleReviews = async () => {
      try {
        // Use proxy in development, direct URL in production
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${API_URL}/google-reviews`);

        if (response.ok) {
          const data = await response.json();
          if (data.reviews && data.reviews.length > 0) {
            // Transform Google reviews to match testimonials format
            const formattedReviews = data.reviews.map((review, index) => ({
              id: index + 1,
              name: review.author_name,
              text: review.text,
              rating: review.rating,
              image: review.profile_photo_url
            }));
            setTestimonials(formattedReviews);
            console.log('✅ Loaded', formattedReviews.length, 'Google reviews');
          }
        } else {
          console.log('API response not ok:', response.status);
        }
      } catch (error) {
        console.log('Using mock testimonials - API not available');
        // Keep using mock data on error
      } finally {
        setLoading(false);
      }
    };

    fetchGoogleReviews();
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-amber-900 via-amber-800 to-amber-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            מה הלקוחות שלנו אומרים
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-300 to-amber-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-lg md:text-xl text-amber-100 max-w-3xl mx-auto">
            אלפי לקוחות מרוצים בכל רחבי הארץ
          </p>
        </div>

        {/* Testimonial Cards */}
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            {/* Main Testimonial Card */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 transform transition-all duration-500">
              {/* Quote Icon */}
              <div className="flex justify-center mb-6">
                <div className="bg-amber-100 p-4 rounded-full">
                  <Quote className="w-12 h-12 text-amber-600" />
                </div>
              </div>

              {/* Stars */}
              <div className="flex justify-center gap-2 mb-6">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-7 h-7 fill-amber-400 text-amber-400"
                  />
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-xl md:text-2xl text-gray-700 text-center mb-8 leading-relaxed italic">
                "{testimonials[currentIndex].text}"
              </p>

              {/* Author */}
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {testimonials[currentIndex].name.charAt(0)}
                  </span>
                </div>
                <h4 className="text-2xl font-bold text-amber-900">
                  {testimonials[currentIndex].name}
                </h4>
                <p className="text-gray-600 mt-1">לקוח מרוצה</p>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-1/2 md:-right-6 md:translate-x-0 bg-white hover:bg-amber-50 text-amber-600 p-4 rounded-full shadow-xl transition-all duration-300 hover:scale-110 z-10"
              aria-label="המלצה קודמת"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 translate-x-1/2 md:-left-6 md:translate-x-0 bg-white hover:bg-amber-50 text-amber-600 p-4 rounded-full shadow-xl transition-all duration-300 hover:scale-110 z-10"
              aria-label="המלצה הבאה"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-12 h-4 bg-white'
                    : 'w-4 h-4 bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`עבור להמלצה ${index + 1}`}
              />
            ))}
          </div>

          {/* All Testimonials Preview */}
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                onClick={() => setCurrentIndex(index)}
                className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 cursor-pointer transition-all duration-300 border-2 ${
                  index === currentIndex
                    ? 'border-white shadow-2xl scale-105'
                    : 'border-white/20 hover:border-white/50 hover:bg-white/20'
                }`}
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-300 text-amber-300" />
                  ))}
                </div>
                <p className="text-white/90 text-sm mb-4 line-clamp-3">
                  "{testimonial.text}"
                </p>
                <p className="text-amber-200 font-semibold">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
